import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import { FaCheck, FaSearch } from "react-icons/fa";
import { FORUM, TRANSITION } from "../Theme/styles";
import { CATEGORIES, categoryForRole } from "../categories";
import { isRcon } from "../permissions";

const NO_TEAM = "Pozostali";

const zespolZeRol = (roleNames) =>
  (roleNames || []).find((n) => CATEGORIES.some((c) => c.discordRole === n)) || null;

const FALLBACK_CATEGORY = "uncategorized";

const categoryKeyFor = (nazwaRoli) => categoryForRole(nazwaRoli) || FALLBACK_CATEGORY;

const AssigneePicker = ({ people, assignments, onChange, categoryName, categoryOfBoard }) => {
  const [scope, setZakres] = useState("team");
  const [search, setSzukaj] = useState("");

  const grupy = useMemo(() => {
    const mapa = new Map();
    for (const o of people) {
      // Serwer moze nie rozpoznac zespolu (rola nazwana inaczej), wiec
      // probujemy jeszcze raz po wszystkich rolach czlonka.
      const name = o.team || zespolZeRol(o.roleNames) || NO_TEAM;
      if (!mapa.has(name)) mapa.set(name, []);
      mapa.get(name).push(o);
    }
    return Array.from(mapa.entries())
      .sort((a, b) => {
        if (a[0] === NO_TEAM) return 1;
        if (b[0] === NO_TEAM) return -1;
        if (a[0] === categoryName) return -1;
        if (b[0] === categoryName) return 1;
        return a[0].localeCompare(b[0], "pl");
      })
      .map(([name, czlonkowie]) => ({
        name,
        key: categoryKeyFor(name),
        czlonkowie: czlonkowie.sort((x, y) =>
          (x.nickname || "").localeCompare(y.nickname || "", "pl")
        ),
      }));
  }, [people, categoryName]);

  const hasTeams = grupy.some((g) => g.name !== NO_TEAM);

  const hasTeam = (key) =>
    assignments.some((p) => p.kind === "team" && p.team === key);
  const hasPerson = (id) =>
    assignments.some((p) => p.kind === "person" && p.assigned_to === id);


  const togglePerson = (person) => {
    onChange(
      hasPerson(person.id)
        ? assignments.filter((p) => !(p.kind === "person" && p.assigned_to === person.id))
        : [
            ...assignments,
            {
              kind: "person",
              team:
                categoryForRole(person.team) ||
                categoryForRole(zespolZeRol(person.roleNames)) ||
                categoryOfBoard ||
                FALLBACK_CATEGORY,
              assigned_to: person.id,
            },
          ]
    );
  };

  const matches = (o) =>
    (o.nickname || "").toLowerCase().includes(search.trim().toLowerCase());

  const scopes = [
    { key: "team", name: "Zespoły" },
    { key: "person", name: "Osoby" },
  ];

  return (
    <Box>
      <Flex
        bg={FORUM.tlo}
        border={"1px solid"}
        borderColor={FORUM.obramowanie}
        borderRadius={"md"}
        p={"3px"}
        gap={"3px"}
        mb={3}
      >
        {scopes.map((z) => (
          <Button
            key={z.key}
            flex={1}
            size={"sm"}
            variant={"unstyled"}
            h={"30px"}
            fontSize={"sm"}
            fontWeight={scope === z.key ? "600" : "400"}
            color={scope === z.key ? FORUM.tekstJasny : FORUM.tekstPrzygaszony}
            bg={scope === z.key ? FORUM.granat : "transparent"}
            borderRadius={"sm"}
            transition={TRANSITION}
            isDisabled={z.wylaczony}
            title={z.wylaczony ? "Serwer nie podaje przynależności do grup" : undefined}
            _hover={scope === z.key ? {} : { bg: FORUM.plakietka, color: FORUM.tekst }}
            onClick={() => {
              setZakres(z.key);
              setSzukaj("");
            }}
          >
            {z.name}
          </Button>
        ))}
      </Flex>

      {scope === "team" && (
        <Flex gap={2} wrap={"wrap"}>
          {CATEGORIES.filter((c) => !c.rconOnly || isRcon()).map((c) => {
            const selected = hasTeam(c.key);
            return (
              <Flex
                key={c.key}
                align={"center"}
                gap={2}
                px={3}
                py={1.5}
                cursor={"pointer"}
                borderRadius={"md"}
                border={"1px solid"}
                borderColor={selected ? "#6C99FF" : FORUM.obramowanie}
                bg={selected ? "rgba(108,153,255,0.10)" : FORUM.tlo}
                color={selected ? FORUM.tekstJasny : FORUM.tekst}
                transition={TRANSITION}
                _hover={{ borderColor: "rgba(255,255,255,0.18)" }}
                onClick={() =>
                  onChange(
                    hasTeam(c.key)
                      ? assignments.filter((a) => !(a.kind === "team" && a.team === c.key))
                      : [...assignments, { kind: "team", team: c.key }]
                  )
                }
              >
                <Text fontSize={"sm"}>{c.name}</Text>
                {c.key === categoryOfBoard && (
                  <Text fontSize={"10px"} color={"#6C99FF"}>
                    ta tablica
                  </Text>
                )}
                {selected && <FaCheck color="#6C99FF" size={10} />}
              </Flex>
            );
          })}
        </Flex>
      )}

      {scope === "person" && (
        <Box>
          <InputGroup size={"sm"} mb={2}>
            <InputLeftElement pointerEvents={"none"} h={"32px"}>
              <FaSearch color={FORUM.tekstPrzygaszony} size={12} />
            </InputLeftElement>
            <Input
              h={"32px"}
              placeholder={"Szukaj po nicku"}
              value={search}
              onChange={(e) => setSzukaj(e.target.value)}
            />
          </InputGroup>

          <Flex direction={"column"} gap={1} maxH={"220px"} overflowY={"auto"}>
            {grupy.map((g) => {
              const pasujacy = g.czlonkowie.filter(matches);
              if (pasujacy.length === 0) return null;
              return (
                <Box key={g.name}>
                  {hasTeams && (
                    <Text
                      fontSize={"10px"}
                      letterSpacing={"0.08em"}
                      textTransform={"uppercase"}
                      color={FORUM.tekstPrzygaszony}
                      mt={2}
                      mb={1}
                      px={1}
                    >
                      {g.name}
                    </Text>
                  )}
                  <Flex direction={"column"} gap={1}>
                    {pasujacy.map((o) => (
                      <Flex
                        key={o.id}
                        align={"center"}
                        gap={2}
                        px={2}
                        py={1.5}
                        borderRadius={"md"}
                        cursor={"pointer"}
                        bg={hasPerson(o.id) ? "rgba(108,153,255,0.10)" : "transparent"}
                        transition={TRANSITION}
                        _hover={{
                          bg: hasPerson(o.id) ? "rgba(108,153,255,0.16)" : FORUM.plakietka,
                        }}
                        onClick={() => togglePerson(o)}
                      >
                        <Avatar size={"xs"} name={o.nickname} src={o.avatar} />
                        <Text fontSize={"sm"} color={FORUM.tekst} noOfLines={1} flex={1}>
                          {o.nickname}
                        </Text>
                        {hasPerson(o.id) && <FaCheck color="#6C99FF" size={11} />}
                      </Flex>
                    ))}
                  </Flex>
                </Box>
              );
            })}
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default AssigneePicker;
